import classNames from "classnames";
import { useDetailsPanelContext } from "PFApp/booking/components/details_panel";
import useDebounce from "PFCore/helpers/use_debounce";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { hideAll } from "tippy.js";

import { useBookingsOverviewContext } from "../../../context/bookings_overview_context";
import css from "../bars.module.scss";
import BasicBar from "../basic_bar";
import { BookingBarContent } from "./booking_bar_content";
import { useBookingBarContext } from "./booking_bar_provider";
import { useBookingBarTooltip } from "./use_booking_bar_tooltip";

export interface BookingBarContainerProps {
  onMouseDown?: (event: MouseEvent) => void;
  onLeftEdgeMouseDown?: (event: MouseEvent) => void;
  onRightEdgeMouseDown?: (event: MouseEvent) => void;
  onHover?: VoidFunction;
  isDisabled: boolean;
  error?: string;
  classes?: Record<string, string>;
  roleBookingsIds?: number[];
}

export const BookingBarContainer = ({
  onMouseDown,
  onLeftEdgeMouseDown,
  onRightEdgeMouseDown,
  onHover,
  isDisabled,
  error,
  classes = {},
  roleBookingsIds
}: BookingBarContainerProps): JSX.Element => {
  const { detailsData, openDetailsPanel, closeDetailsPanel } = useDetailsPanelContext();
  const {
    bar: { left, width, top, id },
    isEditable,
    draggingMode,
    category
  } = useBookingBarContext();
  const { splitMode } = useBookingsOverviewContext();

  const barRef = useRef(null);
  const [hover, setHover] = useState(false);

  const { tooltipContent, tooltipProps } = useBookingBarTooltip({
    hover,
    isDisabled,
    error
  });

  useEffect(() => {
    if (!hover) {
      hideAll();
    }
  }, [hover]);

  const debouncedSetHover = useDebounce(() => {
    onHover?.();
    setHover(true);
  }, 200);

  const handleClick = () => {
    if (splitMode) {
      return;
    }
    if (detailsData?.id === id && detailsData.type === "booking") {
      closeDetailsPanel();
    } else {
      openDetailsPanel({ id, type: "booking", roleBookingsIds });
    }
  };

  const handleHover = async () => {
    debouncedSetHover();
  };

  const handleCancelHover = async () => {
    debouncedSetHover.cancel();
    setHover(false);
  };

  const isBarInteractive = !draggingMode && !isDisabled && !!category && !error;

  return (
    <BasicBar
      barRef={barRef}
      left={left}
      top={top}
      width={width}
      onClick={isBarInteractive ? handleClick : undefined}
      className={classNames(classes.root, {
        [css.noTransition]: draggingMode && ["start", "end"].includes(draggingMode),
        [css.hover]: hover,
        [css.cursorResizing]: !!draggingMode && draggingMode !== "start_end"
      })}
      tooltipContent={tooltipContent}
      tooltipProps={tooltipProps}
      onMouseEnter={isBarInteractive ? handleHover : undefined}
      onMouseLeave={isBarInteractive ? handleCancelHover : undefined}
      onMouseDown={isBarInteractive && isEditable ? onMouseDown : undefined}
    >
      <BookingBarContent
        hover={hover}
        onLeftEdgeMouseDown={onLeftEdgeMouseDown}
        onRightEdgeMouseDown={onRightEdgeMouseDown}
        classes={classes}
      />
    </BasicBar>
  );
};
